import { cn } from '@latitude/token-utils';
import type { ElementType } from 'react';

import { Box, type BoxProps } from './Box';
import { GAP, type SpacingStep } from './spacing';

type StackAlign = 'start' | 'center' | 'end' | 'stretch' | 'baseline';
type StackJustify =
  | 'start'
  | 'center'
  | 'end'
  | 'between'
  | 'around'
  | 'evenly';

const ALIGN: Record<StackAlign, string> = {
  start: 'items-start',
  center: 'items-center',
  end: 'items-end',
  stretch: 'items-stretch',
  baseline: 'items-baseline',
};

const JUSTIFY: Record<StackJustify, string> = {
  start: 'justify-start',
  center: 'justify-center',
  end: 'justify-end',
  between: 'justify-between',
  around: 'justify-around',
  evenly: 'justify-evenly',
};

type StackOwnProps = {
  direction?: 'vertical' | 'horizontal';
  gap?: SpacingStep;
  align?: StackAlign;
  justify?: StackJustify;
};

// Public API (design.md scope: Stack/Inline/Grid/GridItem). 1D flex, NO wrap
// — wrapping is Inline's job. No `wrap` prop: adding one would collapse the
// Stack/Inline distinction that lets a reviewer tell them apart at a glance
// (design.md goal #1 / "why this ranking matters").
export type StackProps<E extends ElementType = 'div'> = StackOwnProps &
  Omit<BoxProps<E>, keyof StackOwnProps>;

export const Stack = <E extends ElementType = 'div'>({
  direction = 'vertical',
  gap,
  align,
  justify,
  className,
  ...rest
}: StackProps<E>) => {
  return (
    // @ts-expect-error - see Center.tsx for why: TS can't reduce two
    // independently-computed Omit<ComponentPropsWithoutRef<E>, ...>
    // expressions to prove they're structurally identical when E is an
    // unresolved generic, even though StackProps<E> is defined in terms of
    // BoxProps<E>.
    <Box<E>
      className={cn(
        'flex',
        direction === 'vertical' ? 'flex-col' : 'flex-row',
        align !== undefined && ALIGN[align],
        justify !== undefined && JUSTIFY[justify],
        gap !== undefined && GAP[gap],
        className,
      )}
      {...rest}
    />
  );
};

Stack.displayName = 'Stack';
